import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { Row, Alert, Card, Col } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropzone from "react-dropzone";
import { OPP_CONSTANTS } from "../../constants/OppContants";
import { humaniseBytes } from "../../utils/formatters";
import "../../MediaPreview.css";
import { addResourceFiles, getSignedURL } from "../../services/fetchApis";
import { uploadFileviaSignedURL } from "../../services/mediaClient";

function AttachmentComponent({
  orderId,
  okTitle,
  uploadedMediaFiles,
  handleUploadSuccess,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMessage] = useState(null);
  const [selectedMediaFiles, setSelectedMediaFiles] = useState([]);
  const [isAllUploaded, setAllUploaded] = useState(false);

  const MAX_LIMIT = OPP_CONSTANTS.resources.MAX_LIMIT;
  const MAX_FILE_SIZE = OPP_CONSTANTS.resources.MAX_FILE_SIZE; // for a 2MB max size

  useEffect(() => {
    const processedMediaFiles = [];
    uploadedMediaFiles.map((mediaFile) => {
      const uploadedMediaFile = {
        ...mediaFile,
        isUploaded: true,
        isSynced: true,
      };
      processedMediaFiles.push(uploadedMediaFile);
    });

    setSelectedMediaFiles(processedMediaFiles);
  }, [uploadedMediaFiles]);

  useEffect(() => {
    if (selectedMediaFiles.length > 0) {
      const unSyncFiles = selectedMediaFiles.filter(
        (file) => file.isSynced !== true
      );
      setAllUploaded(unSyncFiles.length === 0);
    } else {
      setAllUploaded(false);
    }
  }, [selectedMediaFiles]);

  const handleOkSubmit = () => {
    setErrorMessage(null);
    handleUploadResources();
  };

  const addMediaFile = (file) => {
    const mediaFIle = selectedMediaFiles.filter(
      (mediaFile) => mediaFile.fileName === file.fileName
    );

    if (mediaFIle.length > 0) {
      setErrorMessage(OPP_CONSTANTS.messages.duplicateMedia);
      return;
    }

    setSelectedMediaFiles((prevMediaFile) => [...prevMediaFile, file]);
  };

  const removeMediaFile = (mediaFileToRemove) => {
    setSelectedMediaFiles((prevMediaFile) =>
      prevMediaFile.filter(
        (mediaFile) => mediaFile.fileName !== mediaFileToRemove.fileName
      )
    );
    setErrorMessage(null);
  };

  const uploadAllFilesToBucket = async () => {
    setIsLoading(true);
    const processedMediaFiles = [];
    for (let i = 0; i < selectedMediaFiles.length; i++) {
      let mediaFile = selectedMediaFiles[i];

      if (mediaFile.isUploaded) {
        processedMediaFiles.push(mediaFile);
        continue;
      }

      let uploadStatus = false;

      const signedUrlObject = await getSignedURL(
        orderId,
        mediaFile.fileName,
        mediaFile.fileType
      );

      if (signedUrlObject && signedUrlObject.data) {
        // uploadStatus = true;
        uploadStatus = await uploadFileviaSignedURL(
          signedUrlObject.data,
          mediaFile
        );
      }

      if (uploadStatus) {
        mediaFile = {
          ...mediaFile,
          isUploaded: true,
          cdnPath: signedUrlObject.key,
          cdnUrl: process.env.REACT_APP_AWS_URL + signedUrlObject.key,
        };
      } else {
        let errorMsg = OPP_CONSTANTS.messages.failedToUpload;
        if (
          signedUrlObject &&
          signedUrlObject.toString().includes("TypeError:") &&
          signedUrlObject.toString().includes("ISO-")
        ) {
          errorMsg = OPP_CONSTANTS.messages.invalidFileName;
        }
        setErrorMessage(errorMsg);
      }
      processedMediaFiles.push(mediaFile);
    }
    setSelectedMediaFiles(processedMediaFiles);
    return processedMediaFiles;
  };

  /// update sync value
  const updateUploadedMediaFilesStatus = (mediaFilePaths) => {
    const processedMediaFiles = [];
    for (let i = 0; i < mediaFilePaths.length; i++) {
      let mediaFile = mediaFilePaths[i];
      if (mediaFile.isUploaded) {
        mediaFile = {
          ...mediaFile,
          isSynced: true,
        };
      }
      processedMediaFiles.push(mediaFile);
    }
    const syncedFile = processedMediaFiles.filter(
      (file) => file.isSynced === true
    );
    handleUploadSuccess(syncedFile);
    setSelectedMediaFiles(processedMediaFiles);
  };

  /* Handling uploading files to s3 */
  const handleUploadResources = async () => {
    setErrorMessage(null);
    if (selectedMediaFiles && selectedMediaFiles.length > 0) {
      const mediaFilePaths = await uploadAllFilesToBucket();

      if (mediaFilePaths.length > 0) {
        const modifiedArray = mediaFilePaths.map(
          ({ file, isSynced, isUploaded, src, ...rest }) => rest
        );
        const mediaFinalList = modifiedArray.filter((file) => {
          return file.cdnPath != null;
        });

        if (mediaFinalList && mediaFinalList.length > 0) {
          // Always replaces the values in resources
          const response = await addResourceFiles(orderId, mediaFinalList);
          if (response && response.status === "OK") {
            updateUploadedMediaFilesStatus(mediaFilePaths);
          } else {
            setErrorMessage(OPP_CONSTANTS.messages.failToSubmit);
          }
        }
      }
      setIsLoading(false);
    }
  };

  const handleFilesDrop = (acceptedFiles) => {
    setErrorMessage(null);
    let mediaFiles = acceptedFiles;

    if (acceptedFiles.length > 0) {
      mediaFiles = acceptedFiles.filter((file) => {
        return (
          file.type.startsWith("image/") ||
          file.type.startsWith("video/") ||
          file.type.startsWith("application/")
        );
      });

      if (mediaFiles.length > 0) {
        mediaFiles = mediaFiles.filter((file) => {
          return file.size < MAX_FILE_SIZE;
        });

        if (mediaFiles.length === 0) {
          setErrorMessage(
            OPP_CONSTANTS.messages.exceededMediaLimit.replace(
              /{max_size}/,
              humaniseBytes(MAX_FILE_SIZE)
            )
          );
          return;
        }
      }
    }

    if (mediaFiles.length + selectedMediaFiles.length > MAX_LIMIT) {
      setErrorMessage(
        OPP_CONSTANTS.messages.exceedYamllimit.replace(/{max_count}/, MAX_LIMIT)
      );
      return;
    }

    if (mediaFiles.length === 0) {
      setErrorMessage(OPP_CONSTANTS.messages.invalidMedia);
      return;
    }

    mediaFiles.forEach((file, index) => {
      handleMediaUpload(file);
    });
  };

  const handleMediaUpload = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      addMediaFile({
        src: reader.result,
        file: file,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        isUploaded: false,
        isSynced: false,
        cdnPath: null,
        cdnUrl: null,
      });
    };

    reader.readAsDataURL(file);
  };

  const showPickListCTA = () => {
    return (
      <Dropzone
        onDrop={handleFilesDrop}
        multiple={true}
        accept={{
          "image/*": [],
          "video/*": [".mp4", ".mov", ".wmv", ".avi"],
          "application/pdf": [],
        }}
        onFileDialogCancel={() => setErrorMessage(null)}
        noClick={selectedMediaFiles.length >= MAX_LIMIT}
      >
        {({ getRootProps, getInputProps }) => (
          <div
            {...getRootProps()}
            className="upload-button"
            style={{
              opacity: selectedMediaFiles.length >= MAX_LIMIT ? 0.5 : 1,
            }}
          >
            <input {...getInputProps()} />

            <Col style={{ width: "100%" }}>
              <div
                className="summary-label"
                style={{
                  fontSize: "12px",
                  right: "0",
                  bottom: "0",
                  position: "absolute",
                  fontWeight: "bold",
                }}
              >
                <b>
                  {selectedMediaFiles.length} / {MAX_LIMIT}
                </b>
              </div>
              <Col
                style={{
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <FontAwesomeIcon
                  icon={"fa-circle-plus"}
                  fontSize={36}
                  color={"#FF7929"}
                />
                <div
                  className="summary-value"
                  style={{
                    fontSize: "16px",
                    padding: "10px",
                  }}
                >
                  {OPP_CONSTANTS.btns.addMedia}
                  <div className="summary-label">{`(Max file size: ${humaniseBytes(
                    MAX_FILE_SIZE
                  )})`}</div>
                </div>
              </Col>
            </Col>
          </div>
        )}
      </Dropzone>
    );
  };

  const getFontAwesomeIcon = (fileType) => {
    let typeCategory = fileType;
    let icon = "fa-file-image";
    if (/image\/.*/.test(fileType)) {
      typeCategory = "image";
    } else if (/video\/.*/.test(fileType)) {
      typeCategory = "video";
    }

    switch (typeCategory) {
      case "image":
        icon = "fa-file-image";
        break;
      case "video":
        icon = "fa-file-video";
        break;
      default:
        icon = "fa-file-pdf";

        break;
    }

    return icon;
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "10px",
        }}
      >
        {showPickListCTA()}
      </div>
      {selectedMediaFiles.map((mediaFile, index) => {
        let isUploaded = mediaFile.cdnUrl != null;

        return (
          <Card
            size="small"
            hoverable
            style={{ marginTop: "6px" }}
            bodyStyle={{
              borderRadius: "6px",
              padding: 0,
              backgroundColor: "#ff5f0011",
            }}
          >
            <Row>
              <div
                className="media-detail-container"
                style={{
                  borderRight: `1px solid #ff5f00`,
                }}
              >
                {mediaFile.fileType.startsWith("image") && mediaFile.src ? (
                  <img
                    src={mediaFile.src}
                    className="media-container-preview-image"
                    alt="Modal Media"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={getFontAwesomeIcon(mediaFile.fileType)}
                    style={{
                      fontSize: "48px",
                      paddingLeft: "10px",
                      paddingRight: "10px",
                    }}
                  />
                )}
                <Col style={{ padding: "10px" }}>
                  <div
                    className="summary-value"
                    style={{
                      fontSize: "12px",
                      textAlign: "left",
                      color: isLoading
                        ? "#000"
                        : (!isUploaded || !mediaFile.isSynced) && errorMsg
                        ? "red"
                        : "#000",
                    }}
                  >
                    {mediaFile.fileName}
                  </div>
                  {mediaFile.fileSize && (
                    <div
                      className="summary-label"
                      style={{
                        textAlign: "left",
                      }}
                    >
                      Size: <b>{humaniseBytes(mediaFile.fileSize)}</b>
                    </div>
                  )}
                </Col>
              </div>
              <div
                style={{
                  width: "58px",
                  alignSelf: "center",
                  display: "flex",
                  justifyContent: "center",
                }}
                onClick={() =>
                  !isLoading && !isUploaded ? removeMediaFile(mediaFile) : {}
                }
              >
                <FontAwesomeIcon
                  icon={
                    isLoading
                      ? "fa-spinner"
                      : mediaFile.isSynced
                      ? "circle-check"
                      : isUploaded
                      ? "fa-upload"
                      : "fa-trash"
                  }
                  color={
                    mediaFile.isSynced || isUploaded
                      ? "green"
                      : !isLoading && errorMsg
                      ? "red"
                      : "gray"
                  }
                  style={{ fontSize: "24px" }}
                />
              </div>
            </Row>
          </Card>
        );
      })}
      <div style={{ paddingTop: "10px", paddingBottom: "10px" }}>
        {errorMsg && <Alert message={errorMsg} type="error" showIcon />}
      </div>
      <div key={"footer_model_popup"} className="opp-button-container">
        <div key={"footer_model_popup_inner"} style={{ display: "flex" }}>
          {okTitle && (
            <div className="opp-button">
              <Button
                onClick={handleOkSubmit}
                disabled={
                  isLoading || selectedMediaFiles.length == 0 || isAllUploaded
                }
              >
                <FontAwesomeIcon
                  icon={isLoading ? "fa-spinner" : "fa-upload"}
                  style={{
                    paddingRight: "10px",
                  }}
                />
                {isLoading ? "Loading..." : okTitle}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default AttachmentComponent;
